import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from 'src/api/client';
import { Helmet } from 'react-helmet-async';
import { create } from 'zustand';
import { CompanyProvider } from 'src/context/company-context';
import { Result } from 'src/antd';
import { TAllowedArcValue } from 'src/api/consts/CriptioArcValues';
import { T, useT } from '@transifex/react';
import { useQuery } from '@tanstack/react-query';
import { setTransifexLocale } from 'src/components/I18n';
import {
  getLoginMethodOverride,
  isDevSectionFlagsOverride,
  isDemoMode,
} from 'src/api/state';
import { seedDemoFinnish } from 'src/i18n/demoFinnish';
import { isDarkBackground } from 'src/theme/isDarkBackground';
import { TENANT_BG_THEMING_ENABLED } from 'src/theme/tenantTheme';
import { setDatetimeLocale } from '../utils';
import { getCrmHostname } from './brandColorCache';
import { LoadingPage } from '../components/shared';
import { TBorderSize } from '../theme/borders';

type TValue = {
  id: string;
  name: string;
  phone_number: string;
  country: string;
  default_language: string;
  login_logo: string;
  // 'suomifi' renders the dedicated Suomi.fi surface (SuomiFiLogin). A tenant
  // can also expose Suomi.fi under 'user', where CriptioLogin renders it
  // alongside the other providers. Under 'none' NO login surface renders at
  // all, so a Suomi.fi entry in `authentication_urls` stays invisible — that is
  // tenant configuration, not a frontend condition. The dev pill can force this
  // value locally (see getLoginMethodOverride / applyDevLoginMethodOverride).
  login_method: 'none' | 'user' | 'bankid' | 'email_otp' | 'suomifi';
  login_method_user: 'pre_auth_search' | 'post_auth_search';
  datafordeler_enabled?: boolean;
  logo: string;
  // Platform-level Company logo (usually a square brand mark), distinct from
  // the wide `logo` wordmark. Preferred as the favicon source when present.
  icon?: string;
  primary_color: string;
  secondary_color: string;
  waste_fractions: [
    {
      id: string;
      name: string;
      waste_category: string;
      waste_substance: string;
      waste_types: [
        {
          id: string;
          name: string;
        },
      ];
    },
  ];
  crm_custom_text_login_title: string;
  crm_custom_text_login: string;
  page_description_settings: {
    title?: string;
    page: 'login' | 'home';
    sections: {
      title?: string;
      description?: string;
      link_label?: string;
      link_url?: string;
      background_image?: string;
      order?: number;
    }[];
  }[];
  crm_custom_text_home_title: string;
  crm_custom_text_home: string;
  crm_enable_container_notification_providers: string[];
  crm_enable_products?: boolean;
  // REQ-3: gates the "My account" relationship-overview nav section.
  crm_enable_account_overview?: boolean;
  // REQ-8: gates the "Preferences" communication-channels nav section.
  crm_enable_comms_preferences?: boolean;
  // REQ-21: gates the "Messages" (two-way conversation) nav section. The route
  // stays reachable by direct URL regardless (only the sidebar entry is gated).
  crm_enable_messaging?: boolean;
  // REQ-7/26: gates the "Reports" (filter + table + export, saved reports,
  // documents) nav section.
  crm_enable_reports?: boolean;
  // REQ-6: adds the business columns + statutory YLVA tab to Reports. No
  // customer_type exists yet, so business reporting is flag-gated, not derived.
  crm_enable_ylva_reports?: boolean;
  // REQ-25/24: gates the "Pools" (shared-service pool editor + partner view)
  // nav section.
  crm_enable_pools?: boolean;
  // REQ-2: gates the visible "Create an account" link on the login screen. The
  // ?register=1 deep-link stays reachable regardless (route not flag-gated).
  crm_enable_registration?: boolean;
  /** Citizen-portal authority-forms section (Myndighedsskemaer). */
  crm_enable_authority_forms?: boolean;
  /** The Self Composting service rollout flag (staff-set on the backend).
   *  Gates the composting nav entry and route here — display only: the
   *  composting API itself 404s while the flag is off, so hiding the section
   *  is politeness, not the wall. Absent (older backend) reads as off. */
  crm_enable_composting?: boolean;
  /** HSY req 98: show each container's access instructions (the office's
   *  navigation-app description + attached files) under the container on the
   *  Containers page. Staff-set on the backend, which also withholds the data
   *  from `property-containers` while off — hiding the block here is display.
   *  Absent (older backend) reads as off. */
  crm_enable_container_instructions?: boolean;
  /** Skip hire (PRODUCT-513): the per-company master switch for every
   *  skip-hire surface — the public `/request` door and its login-page link,
   *  the `rent_stock` booking in the create flow, the Rentals section on
   *  Agreements and the rental requests behind them. Off, or absent on a
   *  backend that predates the field, the portal renders and requests exactly
   *  what it did before skip hire existed. Read it through
   *  `useSkipHireEnabled()` (src/app/useSkipHireEnabled.ts). */
  crm_enable_skip_hire?: boolean;
  /** Skip hire (PRODUCT-485): opens the public `/request` door — email OTP,
   *  request form, indicative price, "My request" thread — for visitors with
   *  no property. The backend derives it from the tenant having a portal-
   *  enabled "Customer request" ticket type (BE-P3-1) and the master flag
   *  above; the portal checks both. Absent = closed. */
  crm_enable_customer_requests?: boolean;
  authentication_urls: {
    acr_values: TAllowedArcValue;
    url: string;
    business: boolean;
    // Suomi.fi / Finnish Trust Network. The backend emits `'suomifi'` — this is
    // the confirmed live value, not a guess. `'ftn'` is kept only because the
    // original frontend scaffold guessed that spelling before any tenant was
    // provisioned; both are treated as Suomi.fi by
    // `isFinnishTrustNetworkProvider` (src/api/eidProviders.ts), which is the
    // single place SuomiFiLogin and CriptioLogin agree on.
    //
    // Unlike mitid/nemid/bankid (Criipto-brokered), Suomi.fi points at the
    // platform's own SAML endpoint, e.g.
    // `https://<platform-host>/suomifi/saml/login`. The query contract is
    // identical, so `buildEidRedirectUrl` covers both.
    provider: 'mitid' | 'nemid' | 'bankid' | 'suomifi' | 'ftn';
  }[];
  crm_pdf_footer?: string;
  main_portal_font_family?: string;
  secondary_portal_font_family?: string;
  main_portal_font?: string;
  secondary_portal_font?: string;
  background_image_for_home_page?: string;
  background_image_for_login_page?: string;
  border_radius?: TBorderSize;
  supported_languages?: string[];
  /** Tenant currency as an uppercase ISO 4217 code ('EUR', 'DKK', 'NOK', ...),
   *  set in the admin panel under Settings > Finance > Currency. Optional here
   *  only because older backends don't send it yet — read it through
   *  `useCompanyCurrency()` (src/app/useCompanyCurrency.ts), which falls back
   *  to DKK so those tenants keep today's output. */
  currency?: string;
};

export type TCompanyStore = {
  data: TValue;
  hostname: string;
};

const useCompanyStore = create<TCompanyStore>()(() => ({}) as TCompanyStore);

export const useCompany = (): TCompanyStore => useCompanyStore();

/**
 * True when the tenant's secondary_color puts the portal in dark mode. Gated by
 * TENANT_BG_THEMING_ENABLED: while tenant background theming is disabled this is
 * always false, so antd (and the rest of the app) stays in the light look.
 */
export const useIsTenantDark = (): boolean => {
  const secondary = useCompany().data?.secondary_color;
  return TENANT_BG_THEMING_ENABLED ? isDarkBackground(secondary) : false;
};

// --- TEMPORARY frontend feature-flag override -------------------------------
// The backend doesn't yet expose `crm_enable_products` on the company config,
// so we force it on for specific tenants here. Keyed on the resolved CRM
// hostname (see getCrmHostname). Only ever forces a flag ON — never off — so a
// real backend value of `true` still wins.
//
// DELETE this block (and the applyFrontendFlagOverrides call below) once the
// backend ships the real `crm_enable_products` flag.
const PRODUCTS_ENABLED_HOSTNAMES = new Set<string>(['']);

const applyFrontendFlagOverrides = (data: TValue, hostname: string): void => {
  if (PRODUCTS_ENABLED_HOSTNAMES.has(hostname)) {
    data.crm_enable_products = true;
  }
};
// ----------------------------------------------------------------------------

// Dev-only: overlay the dev-pill login-method override onto the company config
// so every consumer (User.store's login-surface gate, Property.store, etc.)
// sees the forced method. Only ever set from the dev pill, persisted in
// localStorage, and re-read on each (reload-triggered) company fetch. No-op
// when no override is set, so it's inert in normal use.
const applyDevLoginMethodOverride = (data: TValue): void => {
  const override = getLoginMethodOverride();
  if (override) {
    data.login_method = override;
  }
};

// Dev-only: force every crm_enable_* section flag ON when the dev-pill "show
// all sections" override is set, so the new nav sections render even though
// the tenant's company payload doesn't define the flags (absence = hidden by
// convention — backends roll features out gradually). Forces ON only; the
// real per-tenant defaults are untouched. Inert when the key is unset.
const applyDevSectionFlagsOverride = (data: TValue): void => {
  if (!isDevSectionFlagsOverride()) return;
  data.crm_enable_account_overview = true;
  data.crm_enable_comms_preferences = true;
  data.crm_enable_messaging = true;
  data.crm_enable_reports = true;
  data.crm_enable_ylva_reports = true;
  data.crm_enable_pools = true;
  data.crm_enable_registration = true;
  data.crm_enable_composting = true;
  data.crm_enable_container_instructions = true;
  data.crm_enable_skip_hire = true;
  data.crm_enable_customer_requests = true;
};

// DEMO MODE: force the whole shipped portal surface on and pin the UI to Finnish
// for the PJH client demo on renyy.wastehero.io (staging). Forces every
// crm_enable_* section flag ON (so the nav + pages render with no dev pill) and
// overrides the tenant locale to Finnish so the demo is all-Finnish regardless of
// the tenant's configured default. Gated on isDemoMode() (REACT_APP_DEMO_MODE) —
// completely inert in development/master builds. The i18n gaps (untranslated
// English keys) are filled locally via seedDemoFinnish(), called after the
// locale is applied below.
const applyDemoOverrides = (data: TValue): void => {
  if (!isDemoMode()) return;
  data.crm_enable_account_overview = true;
  data.crm_enable_comms_preferences = true;
  data.crm_enable_messaging = true;
  data.crm_enable_reports = true;
  data.crm_enable_ylva_reports = true;
  data.crm_enable_pools = true;
  data.crm_enable_registration = true;
  data.crm_enable_products = true;
  data.default_language = 'fi';
  data.supported_languages = ['fi'];
};

// Pads the company logo into a square data URL so the browser doesn't squash
// wide/tall logos when scaling to favicon size. Falls back to the raw URL if
// the image fails to load or CORS blocks the canvas readback.
const useSquareFaviconHref = (logoUrl: string | undefined): string => {
  const [padded, setPadded] = useState<{ src: string; href: string } | null>(
    null
  );

  useEffect(() => {
    if (!logoUrl) return undefined;
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      const size = 64;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const scale = Math.min(size / img.width, size / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
      try {
        setPadded({ src: logoUrl, href: canvas.toDataURL('image/png') });
      } catch {
        // tainted canvas — fall through to raw logo URL
      }
    };
    img.src = logoUrl;
    return () => {
      cancelled = true;
    };
  }, [logoUrl]);

  if (padded && padded.src === logoUrl) return padded.href;
  return logoUrl ?? 'https://not-found.png';
};

const FaviconLink = ({ logo }: { logo: string | undefined }) => {
  const href = useSquareFaviconHref(logo);
  return (
    <Helmet>
      <link rel="icon" type="image/png" href={href} />
    </Helmet>
  );
};

export const CompanyStoreProvider = ({
  children,
}: {
  children: JSX.Element;
}): JSX.Element => {
  const t = useT();

  // Resolve the tenant once per mount via the shared resolver, so the company
  // fetch hostname and the startup spinner-colour cache key (src/index.tsx)
  // always agree.
  const hostname = useMemo(() => getCrmHostname(), []);

  const { isLoading, isError, error, data } = useQuery({
    queryKey: ['CompanyStoreProvider', 'GET'],
    // Company config is static per session — don't refire on reconnect/remount.
    staleTime: 60_000,
    queryFn: async () => {
      const d = await apiClient.get(`company/${hostname}`);
      if (d.data.id) {
        applyFrontendFlagOverrides(d.data as TValue, hostname);
        applyDevLoginMethodOverride(d.data as TValue);
        applyDevSectionFlagsOverride(d.data as TValue);
        applyDemoOverrides(d.data as TValue);
        useCompanyStore.setState({ data: d.data as TValue, hostname });
        // Apply the tenant's default locale before children render, so the
        // first paint isn't in source English. We await Transifex's
        // translation fetch so `LoadingPage` covers the switch instead of
        // the user seeing an English-to-Danish flash. Errors are swallowed
        // — a failed translation fetch shouldn't block the company query.
        const initialLocale = (d.data as TValue).default_language;
        if (initialLocale) {
          try {
            await setTransifexLocale(initialLocale);
          } catch {
            /* ignore — fall back to source */
          }
          // DEMO MODE: seed the local Finnish translations for the demoed
          // surfaces AFTER the remote fetch, so untranslated keys don't leak in
          // English. Must run post-fetch (seeding first would mark the locale
          // non-stale and skip the remote fetch). Merges on top; inert when off.
          seedDemoFinnish();
          setDatetimeLocale(initialLocale);
        }
      }
      return d;
    },
  });

  if ((error as $TSFixMe)?.response?.data?.detail === 'Not Found') {
    // the domain is not set to any company.
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100vw',
          height: '100%',
        }}
      >
        <Result
          status="404"
          title={
            <T
              _str="No DNS setup for hostname: {hostName}"
              hostName={hostname}
            />
          }
        />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100vw',
          height: '100%',
        }}
      >
        <Result
          status="500"
          title={<T _str="Error occured: Please try later." />}
        />
      </div>
    );
  }

  if (isLoading) {
    return <LoadingPage />;
  }

  const company = data?.data as TValue;

  return (
    <>
      <FaviconLink logo={company.icon || company.logo} />
      <Helmet>
        <title>
          {t('Self-service - {companyName}', {
            companyName: company.name,
          }) || t('Citizen Portal')}
        </title>
      </Helmet>
      <CompanyProvider>{children}</CompanyProvider>
    </>
  );
};
